'use client';
import { useState, useEffect, useRef } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import clsx from 'clsx';
import ArrowSvg from '@/assets/icons/arrow.svg';
import { IWithClassname } from '@/models/common';
import s from './Dropdown.module.scss';

interface Props<Option> extends IWithClassname {
  options: Option[];
  placeholder?: string;
  paramsKey: string;
}

export const Dropdown = <Option extends { id: number | string; name: string }, >(props: Props<Option>) => {
  const {
    paramsKey,
    placeholder,
    options,
    className,
  } = props;

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const currentValue = searchParams.get(paramsKey);

  useEffect(() => {
    document.addEventListener('click', closeDropdown);

    return () => document.removeEventListener('click', closeDropdown);
  }, []);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const closeDropdown = (e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  const onSelect = (option: Option) => {
    setIsOpen(false);

    const newValue = JSON.stringify(option);
    const params = new URLSearchParams(searchParams);

    params.set('page', '1');

    if (currentValue) {
      if (option.id === JSON.parse(currentValue).id) {
        params.delete(paramsKey);
      } else {
        params.set(paramsKey, newValue);
      }
    } else {
      params.set(paramsKey, newValue);
    }

    replace(`${pathname}?${params.toString()}`);
  };

  return (
    <div ref={dropdownRef} className={clsx(s.dropdown, className)}>
      <div className={s.dropdown__head} onClick={toggleDropdown}>
        {currentValue
          ? <div>{JSON.parse(currentValue).name}</div>
          : <div className={s.dropdown__placeholder}>{placeholder}</div>
        }
        <ArrowSvg className={clsx(s.dropdown__arrow, { [s.dropdown__arrow_reverse]: isOpen })} />
      </div>
      <div className={clsx(s.dropdown__listWrapper, { [s.dropdown__listWrapper_opened]: isOpen })}>
        <ul className={s.dropdown__list}>
          {options.map((option: Option) => {
            return (
              <li
                key={option.id}
                className={s.dropdown__item}
                onClick={() => onSelect(option)}
              >
                {option.name}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};